/**
 * Componente para exibir múltiplas imagens em formato grid
 * 
 * Layouts responsivos baseados na quantidade:
 * - 1 imagem: imagem única (usa ImageMessageBubble)
 * - 2 imagens: grid 1x2 horizontal
 * - 3 imagens: 1 grande + 2 pequenas (L-layout)
 * - 4+ imagens: grid 2x2 com overlay "+X" no último slot
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { ImageOff, Loader2 } from 'lucide-react';
import { MessageAttachment } from '@/pages/tenant/Chat';

interface ImageGridBubbleProps {
  /** Lista de attachments de imagem */
  attachments: MessageAttachment[];
  /** Legenda compartilhada (opcional) */
  caption?: string | null;
  /** Direção da mensagem (entrada ou saída) */
  direction: 'inbound' | 'outbound';
  /** Callback ao clicar em uma imagem para abrir galeria */
  onOpenGallery: (startIndex: number) => void;
}

// Componente para uma célula de imagem no grid
function GridImageCell({
  url,
  index,
  onClick,
  overlay,
  className,
}: {
  url: string;
  index: number;
  onClick: () => void;
  overlay?: React.ReactNode;
  className?: string;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const imageUrl = getStorageUrl(url) || url;

  return (
    <div
      className={cn(
        'relative overflow-hidden cursor-pointer bg-muted/50',
        className
      )}
      onClick={onClick}
    >
      {/* Loading */}
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error */}
      {hasError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted">
          <ImageOff className="h-4 w-4 text-muted-foreground" />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={`Imagem ${index + 1}`}
          className={cn(
            'w-full h-full object-cover',
            'transition-opacity duration-200',
            isLoading ? 'opacity-0' : 'opacity-100'
          )}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      )}

      {/* Overlay (para +X contador) */}
      {overlay && !hasError && !isLoading && (
        <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
          {overlay}
        </div>
      )}

      {/* Hover overlay */}
      {!hasError && !isLoading && !overlay && (
        <div 
          className={cn(
            'absolute inset-0 bg-background/20 opacity-0',
            'hover:opacity-100 transition-opacity'
          )}
        />
      )}
    </div>
  );
}

export function ImageGridBubble({
  attachments,
  caption,
  direction,
  onOpenGallery,
}: ImageGridBubbleProps) {
  // Filtra apenas attachments de imagem válidos
  const images = attachments.filter(att => att.type === 'image' && att.url);
  
  if (images.length === 0) {
    return null;
  }

  // Quantidade extra além das 4 mostradas
  const extraCount = images.length > 4 ? images.length - 3 : 0;

  // Renderiza grid baseado na quantidade
  const renderGrid = () => {
    // 1 imagem: célula única - tamanho aumentado
    if (images.length === 1) {
      return (
        <GridImageCell
          url={images[0].url}
          index={0}
          onClick={() => onOpenGallery(0)}
          className="rounded-lg max-w-[300px] min-w-[100px] aspect-auto"
        />
      );
    }

    // 2 imagens: grid 1x2 - tamanho aumentado
    if (images.length === 2) {
      return (
        <div className="grid grid-cols-2 gap-1 max-w-[300px]">
          {images.map((att, index) => (
            <GridImageCell
              key={att.id}
              url={att.url}
              index={index}
              onClick={() => onOpenGallery(index)}
              className={cn(
                'aspect-square',
                index === 0 ? 'rounded-tl-lg rounded-bl-lg' : 'rounded-tr-lg rounded-br-lg'
              )}
            />
          ))}
        </div>
      );
    }

    // 3 imagens: L-layout (1 grande + 2 pequenas) - tamanho aumentado
    if (images.length === 3) {
      return (
        <div className="grid grid-cols-2 gap-1 max-w-[300px]">
          <GridImageCell
            url={images[0].url}
            index={0}
            onClick={() => onOpenGallery(0)}
            className="row-span-2 aspect-square rounded-tl-lg rounded-bl-lg"
          />
          <GridImageCell
            url={images[1].url}
            index={1}
            onClick={() => onOpenGallery(1)}
            className="aspect-square rounded-tr-lg"
          />
          <GridImageCell
            url={images[2].url}
            index={2}
            onClick={() => onOpenGallery(2)}
            className="aspect-square rounded-br-lg"
          />
        </div>
      );
    }

    // 4+ imagens: grid 2x2 com +X no último slot - tamanho aumentado
    return (
      <div className="grid grid-cols-2 gap-1 max-w-[300px]">
        <GridImageCell
          url={images[0].url}
          index={0}
          onClick={() => onOpenGallery(0)}
          className="aspect-square rounded-tl-lg"
        />
        <GridImageCell
          url={images[1].url}
          index={1}
          onClick={() => onOpenGallery(1)}
          className="aspect-square rounded-tr-lg"
        />
        <GridImageCell
          url={images[2].url}
          index={2}
          onClick={() => onOpenGallery(2)}
          className="aspect-square rounded-bl-lg"
        />
        <GridImageCell
          url={images[3].url}
          index={3}
          onClick={() => onOpenGallery(3)}
          className="aspect-square rounded-br-lg"
          overlay={
            extraCount > 0 ? (
              <span className="text-lg font-bold text-foreground">
                +{extraCount}
              </span>
            ) : undefined
          }
        />
      </div>
    );
  };

  return (
    <div className="space-y-1 -mx-2.5 md:-mx-4">
      {renderGrid()}

      {/* Caption compartilhada - cores fixas WhatsApp para outbound */}
      {caption && (
        <p 
          className={cn(
            'text-sm whitespace-pre-wrap break-words', 
            direction !== 'outbound' && 'text-foreground'
          )}
          style={direction === 'outbound' ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
        >
          {caption}
        </p>
      )}
    </div>
  );
}
